import { IRecord } from "../../types/schema"
import { ICurrencyValue } from "../../utils/currency"
import { getTotals } from "../../utils/getQuoteOrderTotals"
import { isProductExported } from "../../utils/utils"
export { isProductExported }

export function isUserAuthorizedToViewQuoteOrder(
  record?: { branch?: { id: string | null } | null } | null,
  currentUser?: { branch: { id: string | null } | null },
) {
  const isSameBranch = record?.branch?.id === currentUser?.branch?.id
  if (!isSameBranch) {
    return [
      false,
      `branch_mismatch: Orders Branch ID: ${record?.branch?.id || "none"}; Your Branch ID: ${currentUser?.branch?.id || "none"}`,
    ]
  }
  return [true]
}

type ICustomer = IRecord<
  "customers",
  | "name"
  | "account_reference"
  | "account_on_hold"
  | "outstanding_balance_pennies"
  | "credit_limit_pennies"
  | "tax_rate"
  | "publishQuotesByDefault"
  | "memo"
  | "delivery_note_email"
  | "internalCustomer"
  | "analysisCodes"
  | "currency_name"
  | "cash_sales"
> & {
  addresses: IRecord<"addresses", "postcode" | "importer_id">[]
}

type ISubCustomer = IRecord<"sub_customers", "name">

export interface IQuoteOrderRecord {
  id: string
  identifier: string
  purchase_order_reference?: string
  carriage_pennies: number | null
  customer?: ICustomer
  sub_customer?: ISubCustomer
  items: (IItem | IDeletedItem)[]
  address: IAddress
  billing_address?: IAddress
  job?: { id: string }
  orderRequest?: { id: string }
  dpdProduct?: IRecord<"products", "body" | "sale_price_pennies">
  notes?: string
  delivery_note_email?: string
  required_date?: Date
  files?: (Omit<IRecord<"files", "fileName" | "fileboyId" | "notes">, "id"> & {
    _destroy?: boolean
    id?: string
  })[]
  autoEmailOrderAcknowledgementOn?: string
  contact?: IRecord<"contacts", "email">
  currency_name?: ICurrencyValue
  projectRegistrationNumber?: string
  hasOpportunityCertificate?: boolean
  opportunity_name?: string
  opportunityCertificateFile?: IRecord<"files", "fileboyId"> & { _destroy?: boolean }
  showOptionalItemsInCustomerDocuments?: boolean

  paymentMethod?: string
  paymentMethodNotes?: string
  paymentDate?: string
  paymentTakenBy?: { id: string; name?: string }
  paymentLocation?: string
  paymentCashPennies?: number
  paymentCardPennies?: number
  paymentBacsPennies?: number

  warehouseOverride?: IRecord<"warehouses", "disable_barscan" | "name">
  bypassMinMarginApproved?: boolean

  [key: string]: any
}

export type IAddress = Partial<
  IRecord<
    "addresses",
    | "label"
    | "postcode"
    | "contact"
    | "name"
    | "line_1"
    | "line_2"
    | "line_3"
    | "line_4"
    | "city"
    | "county"
    | "country"
    | "email"
    | "phone"
    | "importer_id"
  >
>

export type IDeletedItem = { id: string; $deleted: true }

export type IItem = IRecord<
  "quote_items" | "order_items",
  | "body"
  | "category"
  | "stock_code"
  | "stock_location"
  | "cost_price_pennies"
  | "type"
  | "quantity"
  | "price_pennies"
  | "markUp"
  | "discountPercent"
  | "parent_uid"
  | "fixed_discount_rate"
  | "build_set_uid"
  | "opportunity_certificate_cost_discount_amount_pennies"
> & {
  sage_show_on_customer_documents?: boolean
  build_quantity?: number | null
  is_rich_quote_optional?: boolean
  special_order_item?: boolean
  originalProductId?: string
  specialItem?: boolean
  allocated_quantity: number | null
  notes?: string
  supplierId?: string
  product?: IRecord<
    "products",
    | "stock_code"
    | "body"
    | "cost_price_pennies"
    | "line_item_description"
    | "price_updated_at"
    | "sage_id"
    | "minimum_margin"
    | "minimum_selling_price_pennies"
    | "preferred_supplier_list_price_pennies"
    | "active"
  > & {
    branch: IRecord<"branches", "name" | "currency_name">
    product_locations: IRecord<
      "product_locations",
      "name" | "free_stock" | "warehouse" | "specific_location"
    >[]
    product_group?: IRecord<
      "product_groups",
      "minimum_product_margin" | "products_price_method" | "requires_elevated_min_price_approval"
    >
    suppliers: IRecord<"suppliers", "currency_name">[]
  }
  product_location?: IRecord<
    "product_locations",
    "name" | "free_stock" | "warehouse" | "specific_location"
  >
  buildRequired?: boolean
  price_locked?: boolean
  standard_price_pennies?: number | null
  standard_discount_percent?: number | null
  oppCertDisabled?: boolean
}

export const enum itemTypes {
  sage = 0,
  text = 1,
  custom = 2,
  buildSet = 3,
  buildSetItem = 4,
}

export interface IValidationResult {
  warnings?: string[]
  errors?: Record<string, any>
}

export interface IQuoteItemRegisterOptions {
  validate?(item: IItem, quote: IQuoteOrderRecord): undefined | IValidationResult

  asyncValidate?(item: IItem, quote: IQuoteOrderRecord): Promise<undefined | IValidationResult>

  onSave?(item: IItem, quote: IQuoteOrderRecord): any | IDeletedItem
}

export interface IRowProps<_T extends Record<string, any> = any> {
  data: IItem & { id: string }
  errors: Record<string, any>
  quote: IQuoteOrderRecord
  isCustomerViewing?: boolean
  hideMarginColumn?: boolean
  hidePriceColumn?: boolean

  displayAllocationColumn?: boolean
  rawFunctions: {
    onChangeSilent(partial: Partial<IItem>): void
    handleItemChange(itemId: string, newItem: IItem | ((oldItem: IItem) => IItem)): void
    getErrorsForItem(item: IItem): Partial<IValidationResult>
    handleRemoveItem(item: IItem, opts?: { skipRemoveChildren?: boolean }): void
    registerItem: (item: IItem, options: IQuoteItemRegisterOptions) => () => void
    batchItemChange: (items: IItem[], newItem: (oldItem: IItem) => IItem) => void
    registeredItems: Record<string, IQuoteItemRegisterOptions>
    handleAddItemToBuildSet?: (buildUid: string) => void
    loadProductDataForSageItemRow: (pid: string) => Promise<{
      item: Partial<IItem>
    }>
    handleAddBOMItemRow: () => void
  }
  renderModal: (data?: {
    Component: (item: { onResolve: (data: any) => any }) => any
  }) => Promise<any>
  type: string

  registerItem(options: IQuoteItemRegisterOptions): void

  onChange(partial: Partial<IItem>): void

  rawOnChange(partial: IItem): void

  onRemove(): void

  onAppendItem?(item: Partial<IItem>): void
}

export interface ICustomerHeadingFieldsProps {
  quote: IQuoteOrderRecord
  errors: any

  onChange(quote: Partial<IQuoteOrderRecord>): void
  onChangeDpdProduct(data?: {
    delivery_method?: string
    dpdProduct?: IQuoteOrderRecord["dpdProduct"]
    carriage_pennies?: IQuoteOrderRecord["carriage_pennies"]
  }): void
}

export function isDeleted(item: IItem | IDeletedItem): item is IDeletedItem {
  return "$deleted" in item
}

export function filterDeleted(items: (IItem | IDeletedItem)[]): IItem[] {
  return items.filter(item => !isDeleted(item)) as any
}

export const blankNewItem = {
  stock_code: "",
  stock_location: "",
  body: "",
  category: "",
  quantity: 1,
  allocated_quantity: 1,
  cost_price_pennies: null,
  price_pennies: null,
  markUp: null,
  discountPercent: null,
}

export function calculateMarginFromPrice(costPricePennies = 0, quotedPricePennies = 0) {
  if (costPricePennies === quotedPricePennies) {
    return 0.0
  } else {
    const marginPercent =
      quotedPricePennies === 0 ? -100 : 100 - (costPricePennies / quotedPricePennies) * 100
    return Number((Math.floor(marginPercent * 100) / 100).toFixed(2))
  }
}

export function formatMargin(margin?: number | null) {
  if (margin === null || margin === undefined) {
    return ""
  }
  return margin === 0 ? "0.00" : `${margin.toFixed(2)}%`
}

export function totalPrice(items: (IItem | IDeletedItem)[], priceColumn: keyof IItem) {
  const nonDeletedItems: IItem[] = items.filter(item => !isDeleted(item)) as any
  return nonDeletedItems
    .filter((item: any) => priceColumn in item && item[priceColumn])
    .reduce((acc, cur: any) => (acc += (cur[priceColumn] || 0) * (cur.quantity || 1)), 0)
}

export function totalDiscount(items: (IItem | IDeletedItem)[], priceColumn: keyof IItem) {
  const nonDeletedItems: IItem[] = items.filter(item => !isDeleted(item)) as any

  let discountAmount = 0

  nonDeletedItems.forEach((item: any) => {
    if (item.discountPercent && priceColumn in item && item[priceColumn]) {
      const itemTotal = item[priceColumn] * (item.quantity || 1)
      const itemDiscountMultipler = item.discountPercent / 100
      const itemDiscountAmount = itemTotal * itemDiscountMultipler
      discountAmount += itemDiscountAmount
    }
  })

  return discountAmount
}

export const PRODUCT_SELECT_COLUMN_WIDTH = { min: 200, max: "auto" }
export const BARSCAN_COLUMN_WIDTH = 80
export const QUANTITY_COLUMN_WIDTH = 80
export const PRICE_COLUMN_WIDTH = 90
export const MARGIN_COLUMN_WIDTH = 75
export const DISCOUNT_COLUMN_WIDTH = 100
export const LOCATION_COLUMN_WIDTH = 220

export function scrollToTopMostError(errorKeys: string[], validationResults: IValidationResult) {
  // Scroll to customer > customer reference > item errors
  if (errorKeys.includes("customer")) {
    window.scrollTo({ top: 0, behavior: "smooth" })
  } else if (errorKeys.includes("customer_reference")) {
    window.scrollTo({ top: 0, behavior: "smooth" })
  } else if (errorKeys.includes("paymentLocation") || errorKeys.includes("paymentSplit")) {
    // the payment details box sits in the customer panel
    window.scrollTo({ top: 0, behavior: "smooth" })
  } else if (errorKeys.includes("items")) {
    const key = Object.keys(validationResults.errors?.["items"] || {})[0]
    const element = document.querySelector(`[data-rbd-drag-handle-draggable-id="${key}"]`)
    element?.scrollIntoView?.({ behavior: "auto", inline: "center", block: "center" })
  }
}

export function changeMarginField(
  value: number | null,
  handleMarkupChange: (value: number | null) => void,
) {
  if (value && value < 100) {
    handleMarkupChange(value)
  } else if (!value && value !== 0) {
    handleMarkupChange(null)
  } else if (value && value > 100) {
    //
  }
}

/**
 * Filters record items of product type, returning an array of items that share the same product as
 * the parent; Also filters out deleted items and does not return the parent item.
 * @param data A quote or order record with items array
 * @param parentItem IItem with - type, product, id attributes
 * */
export function getProductItemChildren(
  data: { items: (IItem | IDeletedItem)[] },
  parentItem?: IItem,
) {
  if (!parentItem || parentItem.type !== itemTypes.sage) {
    return []
  }
  return filterDeleted(data.items).filter(data => itemIsChildOf(data, parentItem))
}

/**
 * @param locations array of product locations with ids
 * @param data quote / order with items
 * @param itemId itemId to not include in filter
 * @returns array of product locations that are not being used
 */
export function filterUsedLocations<T extends IRecord<"product_locations">>(
  locations: T[],
  data: { items: (IItem | IDeletedItem)[] },
  itemId: string,
) {
  const items = filterDeleted(data.items).filter(item => item.id !== itemId)
  const ids = items.map(item => item.product_location?.id).filter(Boolean)
  return locations.filter(location => !ids.includes(location.id))
}

/**
 *
 * @param items Array of quote or order items.
 * @returns An array of the items where duplicated product items have been merged together and the
 * quantity combined
 */
export function quoteOrderGroupedItems<
  T extends {
    [key: string]: any
    product: { id: string }
    type: number | null
    quantity: string | number | null
    allocated_quantity: string | number | null
    product_location?: IRecord<"product_locations", "name" | "free_stock">
    parent_uid?: string
    childItems?: any[]
    originalQty?: number
    build_set_uid: string
  },
>(
  items?: T[],
): (T & {
  locations: (IRecord<"product_locations", "name" | "free_stock"> & {
    quantity: number
  })[]
  childItems: T[]
  originalQty?: number
})[] {
  // get the top level items (any item that is not a child, or has no parent)
  const parentItems = items?.filter(
    item =>
      (!itemIsChild(item) || !parentOfItem(item, items)) && item.type !== itemTypes.buildSetItem,
  )
  return parentItems
    ?.map(item => {
      if (item.type === itemTypes.buildSet) {
        const children =
          items?.filter(
            child =>
              child.type === itemTypes.buildSetItem && item.build_set_uid === child.build_set_uid,
          ) ?? []
        return [item, ...children].map(item => ({
          ...item,
          locations: [],
        }))
      }

      const children = items?.filter(child => itemIsChildOf(child, item)) ?? []

      const childQ =
        children.reduce((quantity, child) => quantity + (Number(child.quantity) || 0), 0) ?? 0
      const childAQ =
        children.reduce(
          (quantity, child) => quantity + (Number(child.allocated_quantity) || 0),
          0,
        ) ?? 0

      // Locations are grouped, includes the parent location and all child locations,
      // plus each child's quantity with that location
      return [
        {
          ...item,
          quantity: Number(item.quantity || 0) + childQ, // modified to simplify the changes that would effect things like
          // the getTotals function
          allocated_quantity: Number(item.allocated_quantity || 0) + childAQ,
          originalQty: Number(item.quantity), // the parent item quantity (orderAcknowledgement PDF)
          originalAllocatedQty: Number(item.allocated_quantity), // the parent item allocated quantity
          locations: [
            { ...item.product_location, quantity: Number(item.quantity) },
            ...children.map(child => ({
              ...(child.product_location ?? {}),
              quantity: Number(child.quantity),
            })),
          ],
          childItems: children, // returns the child items (used in orderAcknowledgement PDF)
        },
      ]
    })
    .reduce((acc, items) => acc.concat(items as any), [] as any[]) as (T & {
    locations: (IRecord<"product_locations", "name" | "free_stock"> & {
      quantity: number
    })[]
    childItems: T[]
    originalQty?: number
  })[]
}

/**
 *
 * @param items Array of quote or order items.
 * @returns An array of the items where duplicated product items have been merged together and the
 * quantity combined
 */
export function quoteOrderSortByGroupedItems<
  T extends {
    [key: string]: any
    weight: number
    stock_code: string
  },
>(items?: T[]) {
  const stockCodeSorted = items?.sort((itemA, itemB) =>
    itemA.stock_code.localeCompare(itemB.stock_code),
  )
  return stockCodeSorted?.sort((itemA, itemB) => {
    if (itemA.stock_code === itemB.stock_code) {
      return 0
    }
    return itemA.weight - itemB.weight
  })
}

// is the item's parent_uid a parent type
export function itemIsParent(item: { parent_uid?: string }) {
  return item.parent_uid?.startsWith("P")
}

// is the item's parent_uid a child type
export function itemIsChild(item: { parent_uid?: string }) {
  return item.parent_uid?.startsWith("C")
}

// is the item a child of the item supplied as the parent?
export function itemIsChildOf(item: { parent_uid?: string }, parentItem: { parent_uid?: string }) {
  if (!item.parent_uid || !parentItem.parent_uid) {
    return false
  }
  return (
    item.parent_uid !== parentItem.parent_uid &&
    itemIsChild(item) &&
    itemIsParent(parentItem) &&
    itemParentUid(item) === itemParentUid(parentItem)
  )
}

// given a child item and an array of items, return the parent for this item from that array.
export function parentOfItem(
  item: { parent_uid?: string },
  potentialParents: { parent_uid?: string }[],
) {
  if (!item.parent_uid) {
    return
  }
  return potentialParents.find(parent => itemIsChildOf(item, parent))
}

// Return the id without the child or parent prefix
export function itemParentUid(item: { parent_uid?: string }) {
  return item.parent_uid?.replace(/C|P/i, "")
}

/**
 *
 * @param type parent or child (prepends P or C)
 * @param parentId required for type of child, optional for type of parent. will make this the uid value after the C or
 *   P
 * @returns a parent or child reference id
 */
export function genItemParentId(type: "parent" | "child", parentId?: string) {
  return `${type === "parent" ? "P" : "C"}${
    type === "parent"
      ? parentId ?? Math.random().toString()
      : itemParentUid({ parent_uid: parentId })
  }`
}

export function calculateDiscountPercent(standardPrice?: number | null, newPrice?: number | null) {
  // Subtract the post-discount price from the pre-discount price. Divide this new number by the pre-discount price.
  // Multiply the resultant number by 100.
  const sPrice = standardPrice ?? 0
  const nPrice = newPrice ?? 0
  return Number((((sPrice - nPrice) / sPrice) * 100).toFixed(2))
}

export function calculateDiscountPrice(
  standardPrice?: number | null,
  discountPercent?: number | null,
) {
  // Take the original price.
  // Multiply it by the discount percentage and divide the result by 100.
  // Subtract the result from the original price.
  const sPrice = standardPrice ?? 0
  const dPercent = discountPercent ?? 0
  return Number((sPrice - sPrice * (dPercent / 100)).toFixed(2))
}

/** The order total in pennies, worked out the same way as the Total shown in the totals box. */
export function getOrderTotalPennies(quote: IQuoteOrderRecord) {
  const items = (quote.items ?? []).flatMap(item =>
    !isDeleted(item) && item.type !== itemTypes.buildSetItem
      ? [
          {
            product: item.product,
            price_pennies: item.price_pennies,
            quantity: item.quantity,
            discountPercent: item.discountPercent,
            opportunity_certificate_cost_discount_amount_pennies:
              item.opportunity_certificate_cost_discount_amount_pennies,
          },
        ]
      : [],
  )
  return Math.round(
    getTotals(items, quote.carriage_pennies, quote.customer?.tax_rate ?? 20).totalPricePennies,
  )
}
